import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ListChecks, RefreshCw, AlertTriangle, Check, CornerDownRight, Repeat, Sparkles, History, CalendarDays, Wallet, Timer, Clock, ChevronDown, Crosshair } from "lucide-react";
import { CtaButton, MonoLabel, SecondaryButton } from "../components/kit";
import { ProcessingLoader } from "../components/ProcessingLoader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import api from "../lib/api";

const EFFORT = {
  S: { cls: "border-[#BFDDB8] text-[#3E7B3E]", label: "Under 1h" },
  M: { cls: "border-[#EAD7A8] text-[#96691E]", label: "Half day" },
  L: { cls: "border-[#BCD4F2] text-[#3565A8]", label: "Full day+" },
};

const RESULT_CHIPS = [
  { value: "better_than_expected", label: "Better than expected" },
  { value: "some_traction", label: "Some traction" },
  { value: "barely_anything", label: "Barely anything" },
  { value: "too_early", label: "Too early to tell" },
];

const FLAG_LABELS = {
  pixel_installed: "Tracking pixel — now marked as installed in your Brand Brain.",
  email_list: "Email list — now marked as live in your Brand Brain.",
  has_website: "Website / landing page — now marked as ready in your Brand Brain.",
  gbp_claimed: "Google Business Profile — now marked as claimed in your Brand Brain.",
  active_socials: "Social profiles — now marked as active in your Brand Brain.",
  ran_ads_before: "First ad campaign — now logged in your Brand Brain.",
};

const eur = (n) => "€" + Math.round(n).toLocaleString("en-US");

const GRAD = "linear-gradient(90deg, #E0603A, #0B67FF)";

const PlanStatsBar = ({ plan }) => (
  <div className="card-soft mt-6 rounded-xl flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-[#EAE5DB] overflow-hidden" data-testid="plan-stats-bar">
    {[
      { icon: CalendarDays, label: "Cycle", value: `Month ${plan.month || 1}`, id: "stat-cycle" },
      { icon: Wallet, label: "Budget", value: `${eur(plan.source_budget)}/mo`, id: "stat-budget" },
      { icon: Timer, label: "Duration", value: `${plan.weeks.length} weeks`, id: "stat-duration" },
    ].map((s) => (
      <div key={s.id} className="flex-1 px-5 py-4" data-testid={s.id}>
        <p className="flex items-center gap-1.5 text-[11px] font-semibold text-[#9A9591]">
          <s.icon size={13} strokeWidth={1.8} /> {s.label}
        </p>
        <p className="text-[14px] font-bold text-[#181621] tabular-nums mt-1">{s.value}</p>
      </div>
    ))}
  </div>
);

const ProgressRing = ({ pct }) => {
  const r = 50;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative w-[128px] h-[128px] shrink-0" data-testid="progress-ring">
      <svg width="128" height="128" viewBox="0 0 128 128">
        <defs>
          <linearGradient id="ap-ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E0603A" />
            <stop offset="100%" stopColor="#0B67FF" />
          </linearGradient>
        </defs>
        <circle cx="64" cy="64" r={r} fill="none" stroke="#F3EFE7" strokeWidth="10" />
        <circle
          cx="64" cy="64" r={r} fill="none"
          stroke="url(#ap-ring-grad)" strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)}
          transform="rotate(-90 64 64)"
          style={{ transition: "stroke-dashoffset 0.5s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[24px] font-extrabold text-[#181621] tabular-nums leading-none">{pct}%</span>
        <span className="text-[10.5px] font-semibold text-[#9A9591] mt-1">complete</span>
      </div>
    </div>
  );
};

const ProgressDashboard = ({ weeks }) => {
  const allTasks = weeks.flatMap((w) => w.tasks);
  const total = allTasks.length;
  const done = allTasks.filter((t) => t.done).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const current = weeks.find((w) => w.tasks.some((t) => !t.done)) || weeks[weeks.length - 1];
  return (
    <div className="card-soft mt-8 p-6 sm:p-7" data-testid="progress-dashboard">
      <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
        <ProgressRing pct={pct} />
        <div className="flex-1 w-full min-w-0">
          <p className="font-bold text-[15px] text-[#181621] tabular-nums" data-testid="progress-summary">
            {done} of {total} tasks done
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4" data-testid="stage-tracker">
            {weeks.map((w) => {
              const wDone = w.tasks.filter((t) => t.done).length;
              const complete = w.tasks.length > 0 && wDone === w.tasks.length;
              const isCurrent = w.week === current.week && !complete;
              return (
                <div
                  key={w.week}
                  className={`rounded-xl px-3 py-2.5 border transition-colors ${
                    isCurrent ? "border-[#181621] bg-white" : "border-[#EAE5DB] bg-[#FBFAF7]"
                  }`}
                  data-testid={`stage-week-${w.week}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${
                        complete ? "bg-[#E0603A]" : isCurrent ? "bg-[#181621]" : "bg-[#E7E2D6]"
                      }`}
                    />
                    <span className={`text-[12.5px] font-bold truncate ${isCurrent || complete ? "text-[#181621]" : "text-[#9A9591]"}`}>
                      {w.theme}
                    </span>
                  </div>
                  <p className="text-[10.5px] font-semibold text-[#9A9591] tabular-nums mt-1">
                    Week {w.week} · {wDone}/{w.tasks.length}
                  </p>
                </div>
              );
            })}
          </div>
          <p className="flex items-center gap-1.5 text-[12.5px] text-[#54505B] mt-4" data-testid="current-focus">
            <Crosshair size={13} strokeWidth={1.8} className="text-[#E0603A]" />
            Current focus: <span className="font-bold text-[#181621]">Week {current.week} — {current.theme}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

const WeekProgress = ({ done, total }) => {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 min-w-[140px]" data-testid="week-progress">
      <span className="text-[11px] font-semibold text-[#9A9591] tabular-nums">{done}/{total}</span>
      <div className="flex-1 h-1.5 rounded-full bg-[#F3EFE7] overflow-hidden min-w-[70px]">
        <div
          className="h-full rounded-full transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%`, background: GRAD }}
        />
      </div>
    </div>
  );
};

const TaskRow = ({ task, onToggle, readOnly = false }) => (
  <div className="px-6 py-4 border-b border-[#F3EFE7] last:border-0 flex items-start gap-4" data-testid={`task-row-${task.id}`}>
    <button
      onClick={() => !readOnly && onToggle(task)}
      disabled={readOnly}
      className={`w-[22px] h-[22px] mt-0.5 rounded-[7px] border flex items-center justify-center shrink-0 transition-colors ${
        task.done ? "bg-[#181621] border-[#181621]" : "border-[#E7E2D6] bg-white"
      } ${readOnly ? "opacity-60 cursor-default" : task.done ? "" : "hover:border-[#181621]"}`}
      data-testid={`task-checkbox-${task.id}`}
      aria-checked={task.done}
      role="checkbox"
    >
      {task.done && <Check size={13} strokeWidth={2.5} className="text-white check-pop" />}
    </button>
    <div className="min-w-0 flex-1">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className={`font-bold text-[14.5px] leading-snug ${task.done ? "line-through text-[#9A9591]" : "text-[#181621]"}`}>
          {task.title}
        </p>
        <span className="flex items-center gap-1.5">
          {task.task_type === "recurring" && (
            <span className="inline-flex items-center gap-1 rounded-full border border-[#E7E2D6] bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#9A9591]" data-testid={`task-recurring-tag-${task.id}`}>
              <Repeat size={10} strokeWidth={2} /> Weekly
            </span>
          )}
          <span className={`inline-flex items-center gap-1 rounded-full border bg-white px-2.5 py-0.5 text-[11px] font-semibold ${EFFORT[task.effort]?.cls || "border-[#E7E2D6] text-[#54505B]"}`}>
            <Clock size={10} strokeWidth={2} /> {EFFORT[task.effort]?.label || task.effort}
          </span>
        </span>
      </div>
      <p className={`text-[12.5px] mt-1 leading-snug ${task.done ? "text-[#C9C6BE]" : "text-[#54505B]"}`}>{task.why}</p>
      {!task.done && /landing/i.test(task.title || "") && (
        <Link to="/app/performance/landing?build=1" className="inline-flex items-center gap-1.5 mt-2 text-[12px] font-bold text-[#96691E] underline hover:text-[#181621]" data-testid={`task-build-landing-${task.id}`}>
          <Sparkles size={11} strokeWidth={2} /> Build it now in Landing Pages →
        </Link>
      )}
      {task.depends_on && (
        <p className="text-[11px] text-[#C9C6BE] mt-1.5 flex items-center gap-1.5">
          <CornerDownRight size={11} strokeWidth={1.8} /> After: {task.depends_on}
        </p>
      )}
    </div>
  </div>
);

const WeekList = ({ weeks, onToggle, readOnly = false }) => {
  const [openWeeks, setOpenWeeks] = useState(() => new Set(weeks.map((w) => w.week)));
  const toggleWeek = (n) =>
    setOpenWeeks((s) => {
      const next = new Set(s);
      next.has(n) ? next.delete(n) : next.add(n);
      return next;
    });
  return (
    <div className="space-y-6 mt-8">
      {weeks.map((w) => {
        const done = w.tasks.filter((t) => t.done).length;
        const open = openWeeks.has(w.week);
        return (
          <div key={w.week} className="card-soft overflow-hidden" data-testid={`week-card-${w.week}`}>
            <button
              onClick={() => toggleWeek(w.week)}
              className={`w-full px-6 py-4 flex flex-wrap items-center justify-between gap-3 text-left transition-colors hover:bg-[#FBFAF7] ${open ? "border-b border-[#F3EFE7]" : ""}`}
              data-testid={`week-header-${w.week}`}
              aria-expanded={open}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="rounded-full border border-[#EAE5DB] bg-[#FBFAF7] px-3 py-1 text-[11px] font-bold text-[#54505B] tabular-nums shrink-0">
                  Week {w.week}
                </span>
                <span className="font-bold text-[14.5px] text-[#181621] truncate">{w.theme}</span>
              </div>
              <div className="flex items-center gap-3">
                <WeekProgress done={done} total={w.tasks.length} />
                <ChevronDown
                  size={16}
                  strokeWidth={2}
                  className={`text-[#9A9591] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                />
              </div>
            </button>
            {open && w.tasks.map((t) => (
              <TaskRow key={t.id} task={t} onToggle={onToggle} readOnly={readOnly} />
            ))}
          </div>
        );
      })}
    </div>
  );
};

const GeneratingCard = ({ month }) => (
  <ProcessingLoader
    eyebrow={month > 1 ? `BUILDING MONTH ${month}` : "BUILDING YOUR 30 DAYS"}
    title="Working on your task…"
    description="Agenzee is preparing the next part of your marketing plan."
    testId="action-plan-generating-state"
  />
);

const MonthCycleCard = ({ month }) => (
  <ProcessingLoader
    eyebrow={`BUILDING MONTH ${month}`}
    title="Working on your task…"
    description="Agenzee is preparing the next part of your marketing plan."
    testId="month-cycle-generating-state"
  />
);

const ReviewDialog = ({ open, onOpenChange, plannedSpend, nextMonth, onSubmit }) => {
  const [spend, setSpend] = useState("");
  const [results, setResults] = useState("");
  const [note, setNote] = useState("");
  const [hardest, setHardest] = useState("");
  const valid = spend !== "" && Number(spend) >= 0 && results && hardest.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-2xl border-[#EAE5DB] max-w-lg" data-testid="month-review-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold text-[#181621]">Month review: 3 questions</DialogTitle>
          <DialogDescription className="text-[13px] text-[#54505B]">
            Your answers shape Month {nextMonth}: what worked stays, what didn't gets rebalanced.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 mt-1">
          <div>
            <p className="text-[11px] font-semibold text-[#9A9591]">1. What did you actually spend?</p>
            <input
              type="number"
              min="0"
              value={spend}
              onChange={(e) => setSpend(e.target.value)}
              placeholder={`The plan called for ${eur(plannedSpend)}/mo`}
              className="mt-2 w-full rounded-xl border border-[#EAE5DB] bg-[#FBFAF7] px-4 py-2.5 text-[14px] text-[#181621] outline-none focus:border-[#181621]"
              data-testid="review-spend-input"
            />
          </div>
          <div>
            <p className="text-[11px] font-semibold text-[#9A9591]">2. What results did you see?</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {RESULT_CHIPS.map((c) => (
                <button
                  key={c.value}
                  onClick={() => setResults(c.value)}
                  className={`rounded-full px-3.5 py-1.5 text-[12.5px] font-bold border transition-colors ${
                    results === c.value ? "bg-[#181621] text-white border-[#181621]" : "bg-white text-[#54505B] border-[#EAE5DB] hover:border-[#181621]"
                  }`}
                  data-testid={`review-result-${c.value}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything specific? (optional)"
              className="mt-2.5 w-full rounded-xl border border-[#EAE5DB] bg-[#FBFAF7] px-4 py-2.5 text-[14px] text-[#181621] outline-none focus:border-[#181621]"
              data-testid="review-note-input"
            />
          </div>
          <div>
            <p className="text-[11px] font-semibold text-[#9A9591]">3. What was hardest to execute?</p>
            <textarea
              value={hardest}
              onChange={(e) => setHardest(e.target.value)}
              rows={3}
              placeholder="Be honest. 'Finding time to film videos', 'the ads dashboard confused me'…"
              className="mt-2 w-full rounded-xl border border-[#EAE5DB] bg-[#FBFAF7] px-4 py-2.5 text-[14px] text-[#181621] outline-none focus:border-[#181621] resize-none"
              data-testid="review-hardest-input"
            />
          </div>
          <CtaButton
            className="w-full justify-center"
            disabled={!valid}
            onClick={() => valid && onSubmit({ actual_spend: Number(spend), results, results_note: note, hardest: hardest.trim() })}
            data-testid="review-submit-btn"
          >
            Build Month {nextMonth}
          </CtaButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ArchivedMonth = ({ doc }) => (
  <div data-testid={`archived-month-${doc.month}`}>
    <div className="card-soft mt-8 p-6" data-testid="archived-review-card">
      <MonoLabel><History size={11} strokeWidth={2} /> MONTH {doc.month} — YOUR REVIEW</MonoLabel>
      <div className="grid sm:grid-cols-3 gap-4 mt-4">
        <div>
          <p className="text-[11px] font-semibold text-[#9A9591]">Spend</p>
          <p className="font-bold text-[14px] text-[#181621] mt-1 tabular-nums">
            {eur(doc.review.actual_spend)} <span className="text-[#9A9591] font-normal">of {eur(doc.review.planned_spend)} planned</span>
          </p>
        </div>
        <div>
          <p className="text-[11px] font-semibold text-[#9A9591]">Results</p>
          <p className="font-bold text-[14px] text-[#181621] mt-1">{doc.review.results}</p>
          {doc.review.results_note && <p className="text-[12px] text-[#54505B] mt-0.5">{doc.review.results_note}</p>}
        </div>
        <div>
          <p className="text-[11px] font-semibold text-[#9A9591]">Hardest part</p>
          <p className="text-[13px] text-[#181621] mt-1 leading-snug">{doc.review.hardest}</p>
        </div>
      </div>
    </div>
    <WeekList weeks={doc.action_plan.weeks} readOnly />
  </div>
);

export default function ActionPlan() {
  const [plan, setPlan] = useState(undefined);
  const [strategy, setStrategy] = useState(undefined);
  const [months, setMonths] = useState([]);
  const [viewMonth, setViewMonth] = useState(null); // null = current
  const [archived, setArchived] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | generating | error | month_generating | month_error
  const [reviewOpen, setReviewOpen] = useState(false);
  const [nextMonth, setNextMonth] = useState(2);
  const pollRef = useRef(null);
  const lastReviewRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const loadMonths = () => api.get("/months").then(({ data }) => setMonths(data)).catch(() => {});

  const reloadAll = async () => {
    const [s, p] = await Promise.all([
      api.get("/strategy").then((r) => r.data).catch(() => null),
      api.get("/action-plan").then((r) => r.data).catch(() => null),
    ]);
    setStrategy(s);
    setPlan(p);
    loadMonths();
  };

  const startPolling = () => {
    stopPolling();
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > 300) {
        stopPolling();
        setPhase("error");
        return;
      }
      try {
        const { data } = await api.get("/action-plan/status");
        if (data.status === "failed") {
          stopPolling();
          setPhase("error");
        } else if (data.status === "complete") {
          stopPolling();
          const res = await api.get("/action-plan");
          setPlan(res.data);
          setPhase("idle");
          toast("Your 30 days are mapped.", { description: "Start with Week 1, top to bottom." });
        }
      } catch {
        /* transient poll failure — keep polling */
      }
    }, 3000);
  };

  const startMonthPolling = (target) => {
    stopPolling();
    let stage = "strategy";
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > 400) {
        stopPolling();
        setPhase("month_error");
        return;
      }
      try {
        if (stage === "strategy") {
          const { data } = await api.get("/strategy/status");
          if (data.status === "failed") {
            stopPolling();
            setPhase("month_error");
          } else if (data.status === "complete") {
            stage = "plan";
          }
        } else {
          const { data } = await api.get("/action-plan/status");
          if (data.status === "failed") {
            stopPolling();
            setPhase("month_error");
          } else if (data.status === "complete") {
            stopPolling();
            await reloadAll();
            setViewMonth(null);
            setPhase("idle");
            toast(`Month ${target} is ready.`, { description: "Fresh strategy and a new 30 days, built from your review." });
          }
        }
      } catch {
        /* keep polling */
      }
    }, 3000);
  };

  const generate = async () => {
    setPhase("generating");
    try {
      await api.post("/action-plan/generate");
      startPolling();
    } catch (err) {
      setPhase(err.response?.status === 400 ? "idle" : "error");
      if (err.response?.status === 400) toast.error("Generate a strategy first.");
    }
  };

  const submitReview = async (payload) => {
    lastReviewRef.current = payload;
    setReviewOpen(false);
    setPhase("month_generating");
    try {
      const { data } = await api.post("/month-review", payload);
      setNextMonth(data.month);
      loadMonths();
      startMonthPolling(data.month);
    } catch (err) {
      setPhase("idle");
      toast.error(err.response?.data?.detail || "Couldn't start the review. Try again.");
    }
  };

  const retryMonthCycle = () => {
    if (lastReviewRef.current) {
      submitReview(lastReviewRef.current);
    } else {
      setPhase("idle");
      setReviewOpen(true);
    }
  };

  useEffect(() => {
    loadMonths();
    (async () => {
      try {
        const { data: job } = await api.get("/action-plan/status");
        const [s, p] = await Promise.all([
          api.get("/strategy").then((r) => r.data).catch(() => null),
          api.get("/action-plan").then((r) => r.data).catch(() => null),
        ]);
        setStrategy(s);
        setPlan(p);
        if (job.status === "pending") {
          setPhase("generating");
          startPolling();
        } else if (job.status === "failed" && !p) {
          setPhase("error");
        }
      } catch {
        setPlan(null);
        setStrategy(null);
      }
    })();
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openArchive = async (m) => {
    setViewMonth(m);
    setArchived(null);
    try {
      const { data } = await api.get(`/months/${m}`);
      setArchived(data);
    } catch {
      setViewMonth(null);
      toast.error("Couldn't load that month.");
    }
  };

  const toggle = async (task) => {
    setPlan((p) => ({
      ...p,
      weeks: p.weeks.map((w) => ({
        ...w,
        tasks: w.tasks.map((t) => (t.id === task.id ? { ...t, done: !t.done } : t)),
      })),
    }));
    try {
      const { data } = await api.patch(`/action-plan/tasks/${task.id}`, { done: !task.done });
      if (data.unlocked) {
        toast("Your profile just got stronger.", {
          description: FLAG_LABELS[data.unlocked] || "Brand Brain updated.",
          icon: <Sparkles size={15} className="text-[#96691E]" />,
        });
      }
    } catch {
      setPlan((p) => ({
        ...p,
        weeks: p.weeks.map((w) => ({
          ...w,
          tasks: w.tasks.map((t) => (t.id === task.id ? { ...t, done: task.done } : t)),
        })),
      }));
      toast.error("Couldn't save that. Try again.");
    }
  };

  const budgetChanged = plan && strategy && plan.source_budget !== strategy.anchor_budget;
  const currentMonth = plan?.month || 1;
  const oneTimeTasks = plan ? plan.weeks.flatMap((w) => w.tasks).filter((t) => t.task_type !== "recurring") : [];
  const allOneTimeDone = oneTimeTasks.length > 0 && oneTimeTasks.every((t) => t.done);
  const startedAt = plan ? new Date(plan.started_at || plan.generated_at) : null;
  const daysPassed = startedAt ? (Date.now() - startedAt.getTime()) / 86400000 : 0;
  const reviewEligible = allOneTimeDone && daysPassed >= 30;
  const showLive = viewMonth === null;

  return (
    <div className="p-6 sm:p-10 max-w-4xl" data-testid="action-plan-page">
      <MonoLabel>CEO Dashboard · Action Plan</MonoLabel>
      <h1 className="mt-5 text-3xl sm:text-4xl leading-tight font-extrabold tracking-[-0.02em]">
        Your action plan
      </h1>
      {plan && showLive && <PlanStatsBar plan={plan} />}

      {(months.length > 0 || currentMonth > 1) && (
        <div className="flex flex-wrap gap-2 mt-6" data-testid="month-pills">
          {months.map((m) => (
            <button
              key={m.month}
              onClick={() => openArchive(m.month)}
              className={`rounded-full px-4 py-1.5 text-[12px] font-bold border transition-colors ${
                viewMonth === m.month ? "bg-[#181621] text-white border-[#181621]" : "bg-white text-[#54505B] border-[#EAE5DB] hover:border-[#181621]"
              }`}
              data-testid={`month-pill-${m.month}`}
            >
              Month {m.month}
            </button>
          ))}
          <button
            onClick={() => setViewMonth(null)}
            className={`rounded-full px-4 py-1.5 text-[12px] font-bold border transition-colors ${
              showLive ? "bg-[#181621] text-white border-[#181621]" : "bg-white text-[#54505B] border-[#EAE5DB] hover:border-[#181621]"
            }`}
            data-testid={`month-pill-current`}
          >
            Month {currentMonth} · current
          </button>
        </div>
      )}

      {!showLive && (archived ? <ArchivedMonth doc={archived} /> : <div className="card-soft mt-8 p-10 animate-pulse h-40" data-testid="archived-loading" />)}

      {showLive && (
        <>
          {reviewEligible && phase === "idle" && plan && (
            <div className="card-soft mt-6 p-6 flex flex-wrap items-center justify-between gap-4 border-l-4 border-l-[#181621]" data-testid="month-review-banner">
              <div>
                <p className="font-bold text-[15px] text-[#181621]">Month {currentMonth} is done. Nice work.</p>
                <p className="text-[13px] text-[#54505B] mt-0.5">Every setup task is checked and 30 days have passed. Answer 3 questions and I'll build Month {currentMonth + 1}.</p>
              </div>
              <CtaButton className="!py-2.5 !px-5 text-[13.5px]" onClick={() => setReviewOpen(true)} data-testid="open-review-btn">
                Review & build Month {currentMonth + 1}
              </CtaButton>
            </div>
          )}

          {budgetChanged && phase === "idle" && (
            <div className="card-soft mt-6 p-5 flex flex-wrap items-center justify-between gap-4" data-testid="budget-changed-banner">
              <div>
                <p className="font-bold text-[14px] text-[#181621]">
                  Your strategy budget changed ({eur(plan.source_budget)} → {eur(strategy.anchor_budget)}).
                </p>
                <p className="text-[12.5px] text-[#54505B] mt-0.5">Rebuild the action plan for the new budget? Your checked tasks will be reset.</p>
              </div>
              <div className="flex gap-2.5">
                <SecondaryButton className="!py-2 !px-4 text-[13px]" onClick={() => setStrategy((s) => ({ ...s, anchor_budget: plan.source_budget }))} data-testid="keep-plan-btn">
                  Keep this plan
                </SecondaryButton>
                <CtaButton className="!py-2 !px-4 text-[13px]" onClick={generate} data-testid="rebuild-plan-btn">
                  Rebuild for {eur(strategy.anchor_budget)}
                </CtaButton>
              </div>
            </div>
          )}

          {plan === undefined && phase === "idle" && (
            <div className="card-soft mt-8 p-10 animate-pulse h-40" data-testid="action-plan-loading" />
          )}

          {phase === "generating" && <GeneratingCard month={currentMonth} />}
          {phase === "month_generating" && <MonthCycleCard month={nextMonth} />}

          {phase === "error" && (
            <div className="card-soft mt-8 p-10 sm:p-14 flex flex-col items-center text-center" data-testid="action-plan-error-state">
              <div className="icon-tile !w-14 !h-14" style={{ background: "var(--amber-bg)" }}>
                <AlertTriangle size={26} strokeWidth={1.8} style={{ color: "var(--amber-fg)" }} />
              </div>
              <p className="mt-5 text-lg font-bold text-[#181621]">Something broke on our side — try again.</p>
              <CtaButton className="mt-7" onClick={generate} data-testid="action-plan-retry-btn">
                <RefreshCw size={15} strokeWidth={1.8} /> Try again
              </CtaButton>
            </div>
          )}

          {phase === "month_error" && (
            <div className="card-soft mt-8 p-10 sm:p-14 flex flex-col items-center text-center" data-testid="month-cycle-error-state">
              <div className="icon-tile !w-14 !h-14" style={{ background: "var(--amber-bg)" }}>
                <AlertTriangle size={26} strokeWidth={1.8} style={{ color: "var(--amber-fg)" }} />
              </div>
              <p className="mt-5 text-lg font-bold text-[#181621]">Month {nextMonth} didn't build — your review is safe.</p>
              <p className="text-[14px] text-[#54505B] mt-1">Hit retry and I'll run it again with the same answers.</p>
              <CtaButton className="mt-7" onClick={retryMonthCycle} data-testid="month-cycle-retry-btn">
                <RefreshCw size={15} strokeWidth={1.8} /> Retry Month {nextMonth}
              </CtaButton>
            </div>
          )}

          {phase === "idle" && plan === null && strategy !== undefined && (
            <div className="card-soft mt-8 p-10 sm:p-14 flex flex-col items-center text-center" data-testid="action-plan-empty-state">
              <div className="icon-tile !w-14 !h-14" style={{ background: "var(--neutral-bg)" }}>
                <ListChecks size={26} strokeWidth={1.8} style={{ color: "var(--neutral-fg)" }} />
              </div>
              {strategy ? (
                <>
                  <p className="mt-5 text-lg font-bold text-[#181621]">No roadmap yet.</p>
                  <p className="text-[14px] text-[#54505B] mt-1">I'll turn your {eur(strategy.anchor_budget)}/mo strategy into 30 days of ordered tasks.</p>
                  <CtaButton className="mt-7" onClick={generate} data-testid="action-plan-generate-btn">
                    Build my 30 days
                  </CtaButton>
                </>
              ) : (
                <>
                  <p className="mt-5 text-lg font-bold text-[#181621]">Strategy first.</p>
                  <p className="text-[14px] text-[#54505B] mt-1">The action plan is built from your strategy — generate that first.</p>
                </>
              )}
            </div>
          )}

          {phase === "idle" && plan && <ProgressDashboard weeks={plan.weeks} />}

          {phase === "idle" && plan && <WeekList weeks={plan.weeks} onToggle={toggle} />}

          {phase === "idle" && plan && !reviewEligible && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={() => setReviewOpen(true)}
                className="text-[13px] font-bold text-[#54505B] underline decoration-[#E7E2D6] underline-offset-4 hover:text-[#181621] transition-colors"
                data-testid="early-review-btn"
              >
                Ahead of schedule? Start Month {currentMonth + 1} early
              </button>
            </div>
          )}
        </>
      )}

      {plan && (
        <ReviewDialog
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          plannedSpend={plan.source_budget}
          nextMonth={currentMonth + 1}
          onSubmit={submitReview}
        />
      )}
    </div>
  );
}
